import { GA_TRACKING_ID, ODDLE_PASS_CLIENT_ID } from '@/libs/config/client'

import {
  selectDetailMenuSite,
  selectIsBot,
  selectTrackers,
} from '@/redux/meta/selector'
import { decodeToken } from '@/utils/jwt'
import { isObjectEmpty } from '@/utils/object'
import { brandIdOf, buildPosthogGroups, safePosthog } from '@/utils/posthog'
import NextRouter from 'next/router'
import { useSelector } from 'react-redux'
import { useGetTrackersQuery } from '@/services/oms'
import { getFullProductDetails, getOrderItemQuantity } from '@/utils/product'
import { selectMenuData } from '@/redux/menu'

const name = 'Oddle Shop'
const safeGtag = (command: string, targetId: string, config?: any): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(command, targetId, config)
  }
}

const safeFbq = (
  command: string,
  targetId: string,
  config?: any,
  options?: { eventID?: string },
): void => {
  if (typeof window !== 'undefined' && window.fbq) {
    if (options === undefined) {
      window.fbq(command, targetId, config)
    } else {
      window.fbq(command, targetId, config, options)
    }
  }
}

const safeSegment = (callback: (analytics: any) => void): void => {
  if (typeof window === 'undefined' || !window.analytics) {
    return
  }
  callback(window.analytics)
}

// Page-view delivery state. Trackers (gtag, Pixel, Segment, PostHog) are injected
// at different times — Segment ~immediately, gtag/Pixel after a deliberate delay.
// notifyPageView is meant to be polled until they all load: each tracker receives
// the view at most once per path, and the flags re-arm when the path changes.
let pageViewPath: string | null = null
const pageViewSent = {
  gtag: false,
  fbq: false,
  segment: false,
}

// Late-tracker delivery for one-shot conversion events (purchase,
// initiate_checkout). The confirmation pages call notifyPurchase exactly once,
// the moment the order query returns (typically 200–800 ms after load), and the
// checkout page calls notifyInitiateCheckout once at mount. gtag and the Pixel
// are injected by ExternalScript only after the trackers query has settled, so
// at that moment they routinely do not exist yet and a plain
// `window.gtag && ...` guard drops the conversion for good (ODD-1163: 0 of 14
// real Kapitan sessions sent a GA4 purchase). Instead, keep the per-tracker
// sends and poll until each tracker shows up, delivering to each at most once.
// Each event type has its own slot with its own timer: one pending entry per
// slot, a newer entry replaces an older one in the same slot, and slots never
// block or replace each other.
const PENDING_POLL_INTERVAL_MS = 300
const PENDING_POLL_MAX_MS = 15000

type PendingSlot = 'purchase' | 'initiate_checkout'

type PendingDelivery = {
  key: string
  gtag?: () => void
  fbq?: () => void
}

const pendingDeliveries: Partial<Record<PendingSlot, PendingDelivery>> = {}
const pendingPollTimers: Partial<
  Record<PendingSlot, ReturnType<typeof setInterval>>
> = {}

const stopPendingPoll = (slot: PendingSlot) => {
  const timer = pendingPollTimers[slot]
  if (timer) {
    clearInterval(timer)
    delete pendingPollTimers[slot]
  }
}

// Sends whatever the available trackers can take. Returns true once nothing
// is left to deliver in this slot.
const flushPendingDelivery = (slot: PendingSlot): boolean => {
  const pending = pendingDeliveries[slot]
  if (!pending) return true
  if (typeof window === 'undefined') return false
  if (pending.gtag && typeof window.gtag === 'function') {
    pending.gtag()
    pending.gtag = undefined
  }
  if (pending.fbq && typeof window.fbq === 'function') {
    pending.fbq()
    pending.fbq = undefined
  }
  if (!pending.gtag && !pending.fbq) {
    delete pendingDeliveries[slot]
    return true
  }
  return false
}

const deliverWhenTrackersReady = (
  slot: PendingSlot,
  pending: PendingDelivery,
) => {
  stopPendingPoll(slot)
  pendingDeliveries[slot] = pending
  if (flushPendingDelivery(slot)) return

  let elapsed = 0
  pendingPollTimers[slot] = setInterval(() => {
    elapsed += PENDING_POLL_INTERVAL_MS
    if (flushPendingDelivery(slot)) {
      stopPendingPoll(slot)
      return
    }
    if (elapsed >= PENDING_POLL_MAX_MS) {
      // Tracker never appeared (not configured, blocked, or Lighthouse). Give
      // up quietly rather than poll forever.
      stopPendingPoll(slot)
      delete pendingDeliveries[slot]
    }
  }, PENDING_POLL_INTERVAL_MS)
}

export type UseTracker = {
  notifyPageView: () => void
  notifyViewContent: (productId?: string) => void
  notifyInitiateCheckout: (storeOrderData?: Oms.StoreOrder | null) => void
  notifyPurchase: (storeOrder?: Oms.StoreOrder | null) => void
  segmentTrackerCheckoutStarted: (data: Record<string, unknown>) => void
  segmentTrackerSignedOut: (payload: { country: string }) => void
  segmentTrackerSignedIn: (payload: {
    id: string
    email: string
    firstName: string
    lastName: string
    phone: string
    continueWithOddlePass: boolean
    country: string
    phoneIsVerified: boolean
    authMethod: string
    action?: string
  }) => void
  segmentTrackerMenuViewed: () => void
  segmentTrackerOrderReminderSaved: (
    payload: Oms.StoreOrder & {
      orderReminderDestination: string
    },
  ) => void
  segmentTrackerOrderedShare: (
    payload: Oms.StoreOrder & { appName: string },
  ) => void
  segmentTrackerOrderViewed: (
    payload: Oms.StoreOrder & { appName?: string; isPurchaser: boolean },
  ) => void
  notifyAddToCart: (payload: {
    menuItem: {
      id: string
      name: string
      imageFullUrls?: string[]
      imageUrls?: string[]
    }
    orderItem: {
      quantity: number
    }
    finalPrice: number
  }) => void
  notifyNewsletterCompleteRegistration: (
    payload: Record<string, unknown>,
  ) => void
  notifyNewsletterLead: () => void
  notifyAccountCompleteRegistration: (payload: { facebookId?: string }) => void
  notifyAccountLead: () => void
  notifyViewCart: (storeOrder: Oms.StoreOrder) => void
  notifyLogin: (method: string) => void
}

const useTracker = (): UseTracker => {
  const menuDetailsInfo = useSelector(selectDetailMenuSite)
  useGetTrackersQuery(menuDetailsInfo?.menu?.id, {
    skip: !menuDetailsInfo?.menu?.id,
  })
  // Replace the general state selector with specific selectors
  const trackers = useSelector(selectTrackers)
  const menuData = useSelector(selectMenuData)
  // const checkoutResults = useSelector(
  //   (state: RootState) => state['@checkout'].checkoutResults
  // )
  // const thankyouResults = useSelector(
  //   (state: RootState) => state['@thankyou'].checkoutResults
  // )

  const meta = useSelector(selectDetailMenuSite)
  const isBot = useSelector(selectIsBot)

  const notifyPageView = () => {
    try {
      // Only run on client side to prevent hydration errors
      if (typeof window === 'undefined') return

      // Re-arm per-tracker delivery whenever the URL changes — INCLUDING the
      // query string. Opening a product modal adds ?productId=... on the same
      // path and must count as its own page view, so we key on pathname+search.
      const path = window.location.pathname + window.location.search
      if (pageViewPath !== path) {
        pageViewPath = path
        pageViewSent.gtag = false
        pageViewSent.fbq = false
        pageViewSent.segment = false
      }

      // Fire only once gtag exists (it loads ~1s late), and only once per path.
      // The token decode + tracker lookup live inside the guard so they run once
      // when gtag fires, not on every poll tick.
      if (typeof window.gtag === 'function' && !pageViewSent.gtag) {
        pageViewSent.gtag = true
        const ga = (trackers || []).find(
          (t) => t.trackingClient === 'GoogleAnalytics',
        )
        let customerId: string | undefined
        const token = window.localStorage.getItem('accessToken')
        if (token) {
          ;({ customerId } = decodeToken(token))
        }
        if (ga && ga.trackingId) {
          safeGtag('config', ga.trackingId, {
            page_location: window.location.href,
            ...(customerId
              ? {
                  user_id: customerId,
                }
              : {}),
          })
        }
        if (GA_TRACKING_ID) {
          GA_TRACKING_ID.split(';').map((tag) => {
            safeGtag('config', tag, {
              page_location: window.location.href,
              ...(customerId
                ? {
                    user_id: customerId,
                  }
                : {}),
            })
          })
        }
      }
      // Same for the Facebook Pixel (also injected ~1s late).
      if (typeof window.fbq === 'function' && !pageViewSent.fbq) {
        pageViewSent.fbq = true
        safeFbq('track', 'PageView')
      }
      // trigger page viewed for segment
      if (
        meta?.customerSegmentEnabled &&
        Boolean((window as any).analytics) &&
        !pageViewSent.segment
      ) {
        pageViewSent.segment = true
        let item: { name: string } | undefined
        // check this condition to detect customer has accessed to production details route
        if (NextRouter.router?.query.productId) {
          const id = NextRouter.router.query.productId
          const menu = menuData || []
          for (const cat of menu) {
            item = cat?.items?.find((catItem) => catItem.id === id)
            if (item) break
          }
        }
        safeSegment((analytics) =>
          analytics.page(
            undefined,
            undefined,
            {
              url: window.location.href,
              // sometime document.title don't have the name of production
              title: item && item.name ? item.name : document.title,
              path: window.location.pathname,
              referrer: document.referrer,
              country: meta.menuCountry,
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name,
              },
              actor: 'consumer',
              accountId: meta.menu?.account?.id,
              accountName: meta.menu?.account?.name,
              brandId: brandIdOf(meta),
              brandName: meta.menu?.name,
            },
            {
              context: { groupId: brandIdOf(meta) },
            },
          ),
        )
        safePosthog((ph) => {
          const groups = buildPosthogGroups(meta)
          ph.capture('$pageview', {
            url: window.location.href,
            title: item && item.name ? item.name : document.title,
            path: window.location.pathname,
            referrer: document.referrer,
            country: meta.menuCountry,
            app: { id: ODDLE_PASS_CLIENT_ID, name },
            actor: 'consumer',
            accountId: meta.menu?.account?.id,
            accountName: meta.menu?.account?.name,
            brandId: brandIdOf(meta),
            brandName: meta.menu?.name,
            ...(groups ? { $groups: groups } : {}),
          })
        })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyViewContent = (productId?: string) => {
    try {
      // Only run on client side to prevent hydration errors
      if (typeof window === 'undefined') return

      const id = productId || (NextRouter.router?.query.productId as string)
      const menu = menuData || []
      const { currencyCode } = meta || {}
      let category: { name: string } | undefined
      let item: Oms.MenuItem | undefined

      for (const cat of menu) {
        item = cat?.items?.find?.((catItem) => catItem.id === id)
        if (item) {
          category = cat
          break
        }
      }

      if (item && category && meta) {
        safeFbq('track', 'ViewContent', {
          value: item.variants?.[0]?.price,
          currency: meta.currencyCode,
          content_name: item.name,
          content_type: 'product',
          content_ids: [item.id],
        })

        const viewContentItemPayload = {
          item_id: item.id,
          item_name: item.name,
          item_brand: meta.menu.name,
          item_category: category.name,
          quantity: 1,
          price: item.variants?.[0]?.price,
        }
        safeGtag('event', 'view_item', {
          items: [viewContentItemPayload],
          currency: currencyCode,
          value: item.variants?.[0]?.price,
        })
        if (meta.customerSegmentEnabled) {
          const productViewedPayload = {
            app: {
              id: ODDLE_PASS_CLIENT_ID,
              name,
            },
            actor: 'consumer',
            productId: viewContentItemPayload.item_id,
            category: viewContentItemPayload.item_category,
            name: viewContentItemPayload.item_name,
            brand: viewContentItemPayload.item_brand,
            price: viewContentItemPayload.price,
            quantity: viewContentItemPayload.quantity,
            value:
              (viewContentItemPayload.price || 0) *
              (viewContentItemPayload.quantity || 0),
            currency: meta.currencyCode,
            menuId: meta.menu.id,
            accountId: meta.menu?.account?.id,
            url: window.location.href,
            ...(item?.imageFullUrls?.[0]
              ? {
                  imageUrl: `${meta.imagePath}product/${item.imageFullUrls[0]}.jpg`,
                }
              : item?.imageUrls?.[0]
                ? {
                    imageUrl: `${meta.imagePath}product/${item.imageUrls[0]}.jpg`,
                  }
                : {}),
            country: meta.menuCountry,
            accountName: meta.menu?.account?.name,
            brandId: brandIdOf(meta),
            brandName: meta.menu?.name,
          }
          safeSegment((analytics) =>
            analytics.track('Product Viewed', productViewedPayload, {
              context: { groupId: brandIdOf(meta) },
            }),
          )
          safePosthog((ph) => {
            const groups = buildPosthogGroups(meta)
            ph.capture('Product Viewed', {
              ...productViewedPayload,
              ...(groups ? { $groups: groups } : {}),
            })
          })
        }
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyInitiateCheckout = (storeOrderData?: Oms.StoreOrder | null) => {
    try {
      if (isObjectEmpty(storeOrderData) || !storeOrderData) {
        return
      }
      if (isBot) return

      const orderItems = storeOrderData.orderItems || []
      const currency = storeOrderData.currencyCode || meta?.currencyCode
      const value = storeOrderData.total
      const pixelPayload = {
        value,
        currency,
        num_items: orderItems.reduce(
          (sum, item) => sum + (item.quantity || 0),
          0,
        ),
        contents: orderItems.map((item) => ({
          id: item.itemId,
          quantity: item.quantity,
          item_price: item.price,
        })),
      }
      const items = orderItems.map((item) => {
        const category = menuData?.find((c) =>
          c.items?.some((menuItem) => menuItem.id === item.itemId),
        )
        return {
          item_id: item.itemId,
          item_name: item.name,
          item_brand: storeOrderData.menuName ?? meta?.menu?.name,
          ...(category?.name ? { item_category: category.name } : {}),
          quantity: item.quantity,
          price: item.price,
        }
      })
      const beginCheckoutPayload = { currency, value, items }
      // Same late-tracker queue as the purchase event, in its own slot.
      deliverWhenTrackersReady('initiate_checkout', {
        key: storeOrderData.id,
        fbq: () =>
          safeFbq('track', 'InitiateCheckout', pixelPayload, {
            eventID: storeOrderData.id,
          }),
        gtag: () => safeGtag('event', 'begin_checkout', beginCheckoutPayload),
      })

      // GTM-compatible ecommerce push, mirroring notifyPurchase.
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({
          event: 'begin_checkout',
          ecommerce: beginCheckoutPayload,
        })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyPurchase = (storeOrderData?: Oms.StoreOrder | null) => {
    try {
      if (isObjectEmpty(storeOrderData) || !storeOrderData) {
        return
      }
      if (isBot) return

      const taxAndCharges = storeOrderData.taxAndCharges || []
      const orderItems = storeOrderData.orderItems || []
      const pixelPayload = {
        value: storeOrderData.total,
        currency: storeOrderData.currencyCode,
        num_items: getOrderItemQuantity(orderItems),
        contents: orderItems.map((order) => {
          const productDetails = getFullProductDetails(menuData, order)
          return {
            id: productDetails.itemId,
            quantity: order.quantity,
            item_price: order.price,
          }
        }),
        ...(storeOrderData.orderNumber
          ? { order_id: storeOrderData.orderNumber }
          : {}),
      }
      const purchasePayload = {
        transaction_id: storeOrderData.id,
        affiliation: storeOrderData.menuName,
        value: storeOrderData.total,
        shipping: storeOrderData.deliveryCost,
        tax: taxAndCharges.reduce((sum, t) => sum + t.amount, 0),
        currency: storeOrderData.currencyCode,
        items: orderItems.map((order) => ({
          item_id: order.itemId,
          item_name: order.name,
          quantity: order.quantity,
          price: order.price,
          item_brand: menuDetailsInfo?.menu?.name,
          item_category: (
            menuData?.find((category) =>
              category.items.find((item) => item.id === order.itemId),
            ) || {}
          ).name,
        })),
      }
      // Delivered now if the trackers are already on the page, otherwise as
      // soon as each one appears (see deliverWhenTrackersReady).
      deliverWhenTrackersReady('purchase', {
        key: storeOrderData.id,
        fbq: () =>
          safeFbq('track', 'Purchase', pixelPayload, {
            eventID: storeOrderData.id,
          }),
        gtag: () => safeGtag('event', 'purchase', purchasePayload),
      })

      // GTM-compatible ecommerce push. safeGtag above pushes gtag's
      // `arguments` object, which a merchant's own GTM container cannot read as
      // an event. Push the standard GA4 Ecommerce shape straight onto
      // window.dataLayer so GTM purchase triggers fire. This is queue-safe:
      // GTM picks it up whenever the container finishes loading, so it does not
      // depend on window.gtag already being present.
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: purchasePayload.transaction_id,
            affiliation: purchasePayload.affiliation,
            value: purchasePayload.value,
            shipping: purchasePayload.shipping,
            tax: purchasePayload.tax,
            currency: purchasePayload.currency,
            items: purchasePayload.items,
          },
        })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerCheckoutStarted = (segment: Record<string, unknown>) => {
    try {
      if (isBot) return
      if (!isObjectEmpty(segment || {})) {
        const checkoutStartedPayload = {
          app: {
            id: ODDLE_PASS_CLIENT_ID,
            name,
          },
          actor: 'consumer',
          country: meta?.menuCountry,
          brandId: brandIdOf(meta),
          brandName: meta?.menu?.name,
          ...segment,
          oddlePassFirstOrder: undefined,
          oddlePassFirstMenuOrder: undefined,
          submittedDate: undefined,
          orderNumber: undefined,
          oddleEatsReferrer: undefined,
          value: segment.total,
          total: undefined,
        }
        safeSegment((analytics) =>
          analytics.track('Checkout Started', checkoutStartedPayload, {
            context: { groupId: brandIdOf(meta) },
          }),
        )
        safePosthog((ph) => {
          const groups = buildPosthogGroups(meta)
          ph.capture('Checkout Started', {
            ...checkoutStartedPayload,
            ...(groups ? { $groups: groups } : {}),
          })
        })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerSignedOut = ({ country }: { country: string }) => {
    if (meta?.customerSegmentEnabled) {
      const signedOutPayload = {
        app: {
          id: ODDLE_PASS_CLIENT_ID,
          name,
        },
        actor: 'consumer',
        country,
        accountId: meta.menu?.account?.id,
        accountName: meta.menu?.account?.name,
        brandId: brandIdOf(meta),
        brandName: meta.menu?.name,
      }
      safeSegment((analytics) => {
        analytics.track('Signed Out', signedOutPayload, {
          context: { groupId: brandIdOf(meta) },
        })
        analytics.reset()
      })
      safePosthog((ph) => {
        const groups = buildPosthogGroups(meta)
        ph.capture('Signed Out', {
          ...signedOutPayload,
          ...(groups ? { $groups: groups } : {}),
        })
        ph.reset()
      })
    }
  }

  const segmentTrackerSignedIn = (payload: {
    id: string
    email: string
    firstName: string
    lastName: string
    phone: string
    continueWithOddlePass: boolean
    country: string
    phoneIsVerified: boolean
    authMethod: string
    action?: string
  }) => {
    try {
      if (meta?.customerSegmentEnabled) {
        const {
          id,
          email,
          firstName,
          lastName,
          phone,
          continueWithOddlePass,
          country,
          phoneIsVerified,
          authMethod,
        } = payload
        if (payload.action !== 'verification') {
          // Standardised consumer identity traits — shared across all
          // consumer-facing apps (Shop, Reserve Customer Booking, VL,
          // Redeemables, Survey). userType stays 'consumer' for every
          // consumer app; loginType collapses to 'email' since the
          // various OTP / Facebook / Oddle Pass flows ultimately key off
          // email identity. Revisit if we surface signin-method
          // distinctions in the UI.
          const identifyTraits = {
            email,
            firstName,
            lastName,
            phone,
            country,
            phoneIsVerified,
            userType: 'consumer',
            loginType: 'email',
          }
          const signedInPayload = {
            app: {
              id: ODDLE_PASS_CLIENT_ID,
              name,
            },
            actor: 'consumer',
            email,
            firstName,
            lastName,
            phone,
            continueWithOddlePass,
            country,
            accountId: meta.menu?.account?.id,
            accountName: meta.menu?.account?.name,
            brandId: brandIdOf(meta),
            brandName: meta.menu?.name,
            phoneIsVerified,
            authMethod,
            userType: 'consumer',
            loginType: 'email',
          }
          safeSegment((analytics) => {
            analytics.identify(id, identifyTraits)
            analytics.track('Signed In', signedInPayload, {
              context: { groupId: brandIdOf(meta) },
            })
          })
          safePosthog((ph) => {
            ph.identify(id, identifyTraits)
            // NOTE: do NOT call posthog.group() here. On a consumer
            // session that would permanently associate the consumer's
            // person profile with the brand/account, conflating the
            // customer's identity with merchant entities they only
            // happen to transact with. Per-event $groups attribution
            // (below, via `$groups` on the capture body) is what we
            // want for B2B2C analytics. Group property names are set
            // separately from the backend via groupIdentify.
            const groups = buildPosthogGroups(meta)
            ph.capture('Signed In', {
              ...signedInPayload,
              ...(groups ? { $groups: groups } : {}),
            })
          })
        }
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerMenuViewed = () => {
    try {
      // Only run on client side to prevent hydration errors
      if (typeof window === 'undefined') return

      if (meta?.customerSegmentEnabled) {
        const menuViewedPayload = {
          app: {
            id: ODDLE_PASS_CLIENT_ID,
            name,
          },
          actor: 'consumer',
          id: meta.menu.id,
          name: meta.menu.name,
          url: window.location.href,
          country: meta.menuCountry,
          accountId: meta.menu?.account?.id,
          accountName: meta.menu?.account?.name,
          brandId: brandIdOf(meta),
          brandName: meta.menu?.name,
        }
        safeSegment((analytics) =>
          analytics.track('Menu Viewed', menuViewedPayload, {
            context: { groupId: brandIdOf(meta) },
          }),
        )
        safePosthog((ph) => {
          const groups = buildPosthogGroups(meta)
          ph.capture('Menu Viewed', {
            ...menuViewedPayload,
            ...(groups ? { $groups: groups } : {}),
          })
        })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyAddToCart = (payload: {
    menuItem: {
      id: string
      name: string
      imageFullUrls?: string[]
      imageUrls?: string[]
    }
    orderItem: {
      quantity: number
    }
    finalPrice: number
  }) => {
    try {
      // Only run on client side to prevent hydration errors
      if (typeof window === 'undefined') return

      const category = menuData?.find((category) =>
        category.items.find((item) => item.id === payload.menuItem.id),
      )
      safeFbq('track', 'AddToCart', {
        value: payload.orderItem.quantity * payload.finalPrice,
        currency: meta?.currencyCode,
        content_name: payload.menuItem.name,
        content_type: 'product',
        contents: [
          {
            id: payload.menuItem.id,
            quantity: payload.orderItem.quantity,
            item_price: payload.finalPrice,
          },
        ],
      })
      const addToCartItemPayload = {
        item_id: payload.menuItem.id,
        item_name: payload.menuItem.name,
        item_brand: meta?.menu?.name,
        item_category: category?.name,
        quantity: payload.orderItem.quantity,
        price: payload.finalPrice,
      }
      safeGtag('event', 'add_to_cart', {
        items: [addToCartItemPayload],
        currency: meta?.currencyCode,
        value: payload.finalPrice,
      })
      if (meta?.customerSegmentEnabled) {
        const productAddedPayload = {
          app: {
            id: ODDLE_PASS_CLIENT_ID,
            name,
          },
          actor: 'consumer',
          productId: addToCartItemPayload.item_id,
          category: addToCartItemPayload.item_category,
          name: addToCartItemPayload.item_name,
          brand: addToCartItemPayload.item_brand,
          price: addToCartItemPayload.price,
          quantity: addToCartItemPayload.quantity,
          value: addToCartItemPayload.price * addToCartItemPayload.quantity,
          currency: meta?.currencyCode,
          menuId: meta?.menu?.id,
          accountId: meta?.menu?.account?.id,
          url: window.location.href,
          ...(payload.menuItem.imageFullUrls?.[0]
            ? {
                imageUrl: `${meta.imagePath}product/${payload.menuItem.imageFullUrls[0]}.jpg`,
              }
            : payload.menuItem.imageUrls?.[0]
              ? {
                  imageUrl: `${meta.imagePath}product/${payload.menuItem.imageUrls[0]}.jpg`,
                }
              : {}),
          country: meta.menuCountry,
          accountName: meta.menu?.account?.name,
          brandId: brandIdOf(meta),
          brandName: meta.menu?.name,
        }
        safeSegment((analytics) =>
          analytics.track('Product Added', productAddedPayload, {
            context: { groupId: brandIdOf(meta) },
          }),
        )
        safePosthog((ph) => {
          const groups = buildPosthogGroups(meta)
          ph.capture('Product Added', {
            ...productAddedPayload,
            ...(groups ? { $groups: groups } : {}),
          })
        })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyNewsletterCompleteRegistration = () => {
    safeFbq('track', 'CompleteRegistration', { content_name: 'newsletter' })
    safeGtag('event', 'complete_registration', {
      event_category: 'engagement',
      event_label: 'newsletter',
    })
  }

  const notifyNewsletterLead = () => {
    safeFbq('track', 'Lead', { content_name: 'newsletter' })
    safeGtag('event', 'lead', {
      event_category: 'engagement',
      event_label: 'newsletter',
    })
  }

  // For non Oddle Pass shop, it is impossible to differentiate between Facebook login or registration since we use the same API and the whole process is invisible to Frontend
  const notifyAccountCompleteRegistration = (payload: {
    facebookId?: string
  }) => {
    safeFbq('track', 'CompleteRegistration', { content_name: 'account' })
    safeGtag('event', 'sign_up', {
      method: payload.facebookId ? 'facebook' : 'email',
    })
  }

  const notifyAccountLead = () => {
    safeFbq('track', 'Lead', { content_name: 'account' })
    safeGtag('event', 'lead', {
      event_category: 'engagement',
      event_label: 'account',
    })
  }

  const segmentTrackerOrderReminderSaved = (payload: Oms.StoreOrder) => {
    try {
      const products = (payload.orderItems || []).map((item) => {
        return {
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          id: item.id,
          orderTags: item.subitemChoices
            ? item.subitemChoices.map((x) => ({
                id: x.name,
                name: x.name,
                price: x.price,
                amount: Number((x.price * x.quantity).toFixed(2)),
                quantity: x.quantity,
              }))
            : null,
        }
      })
      const amount = {
        charges: (payload.taxAndCharges || [])
          .filter((item) => !item.description.includes('GST'))
          .reduce((sum, t) => sum + t.amount, 0),
        discount: (payload.orderPromotions || []).reduce(
          (sum, t) => sum + t.amount,
          0,
        ),
        tax:
          (payload.taxAndCharges || []).filter((item) =>
            item.description.includes('GST'),
          )?.[0]?.amount || 0,
        taxAndCharges: (payload.taxAndCharges || []).reduce(
          (sum, t) => sum + t.amount,
          0,
        ),
        amountProducts: (products || []).reduce((sum, t) => sum + t.price, 0),
      }
      if (meta?.customerSegmentEnabled) {
        const orderReminderPayload = {
          app: {
            id: ODDLE_PASS_CLIENT_ID,
            name,
          },
          actor: 'consumer',
          orderNumber: payload.orderNumber,
          isPickup: payload.isPickup,
          submittedDate: payload.orderDate,
          accountId: meta.menu?.account?.id,
          accountName: meta.menu?.account?.name,
          menuId: meta.menu.id,
          menuName: meta.menu.name,
          storeId: payload.storeId,
          storeName: payload.storeName,
          promoCode: payload?.orderPromotions
            ? payload?.orderPromotions?.[0]?.promoCode
            : '',
          orderId: payload.id,
          charge: amount.charges,
          delivery: payload.deliveryCost,
          deliveryWithTaxCharge: amount.taxAndCharges + payload.deliveryCost,
          discount: amount.discount,
          discountWithTaxCharge: amount.discount + amount.tax,
          subTotal: payload.subtotal,
          subTotalWithCharge: payload.subtotal + amount.tax,
          tax: amount.tax,
          total: payload.total,
          currency: payload.currencyCode,
          products: {
            productItems: products,
            amount: amount.amountProducts,
          },
        }
        safeSegment((analytics) =>
          analytics.track('Order Reminder Saved', orderReminderPayload),
        )
        safePosthog((ph) => {
          const groups = buildPosthogGroups(meta)
          ph.capture('Order Reminder Saved', {
            ...orderReminderPayload,
            ...(groups ? { $groups: groups } : {}),
          })
        })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerOrderedShare = (
    payload: Oms.StoreOrder & { appName: string },
  ) => {
    try {
      if (meta?.customerSegmentEnabled) {
        const products = (payload.orderItems || []).map((item) => {
          return {
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            id: item.id,
            orderTags: item.subitemChoices
              ? item.subitemChoices.map((x) => ({
                  id: x.name,
                  name: x.name,
                  price: x.price,
                  amount: Number((x.price * x.quantity).toFixed(2)),
                  quantity: x.quantity,
                }))
              : null,
          }
        })
        const amount = {
          charges: (payload.taxAndCharges || [])
            .filter((item) => !item.description.includes('GST'))
            .reduce((sum, t) => sum + t.amount, 0),
          discount: (payload.orderPromotions || []).reduce(
            (sum, t) => sum + t.amount,
            0,
          ),
          tax:
            (payload.taxAndCharges || []).filter((item) =>
              item.description.includes('GST'),
            )?.[0]?.amount || 0,
          taxAndCharges: (payload.taxAndCharges || []).reduce(
            (sum, t) => sum + t.amount,
            0,
          ),
          amountProducts: (products || []).reduce((sum, t) => sum + t.price, 0),
        }
        const orderSharedPayload = {
          app: {
            id: ODDLE_PASS_CLIENT_ID,
            name,
          },
          actor: 'consumer',
          orderNumber: payload.orderNumber,
          isPickup: payload.isPickup,
          submittedDate: payload.orderDate,
          accountId: meta.menu?.account?.id,
          accountName: meta.menu?.account?.name,
          menuId: meta.menu.id,
          menuName: meta.menu.name,
          storeId: payload.storeId,
          storeName: payload.storeName,
          promoCode: payload?.orderPromotions
            ? payload?.orderPromotions[0]?.promoCode
            : '',
          orderId: payload.id,
          charge: amount.charges,
          delivery: payload.deliveryCost,
          deliveryWithTaxCharge: amount.taxAndCharges + payload.deliveryCost,
          discount: amount.discount,
          discountWithTaxCharge: amount.discount + amount.tax,
          subTotal: payload.subtotal,
          subTotalWithCharge: payload.subtotal + amount.tax,
          tax: amount.tax,
          total: payload.total,
          currency: payload.currencyCode,
          products: {
            productItems: products,
            amount: amount.amountProducts,
          },
          orderShareDestination: payload.appName,
        }
        safeSegment((analytics) =>
          analytics.track('Order Shared', orderSharedPayload),
        )
        safePosthog((ph) => {
          const groups = buildPosthogGroups(meta)
          ph.capture('Order Shared', {
            ...orderSharedPayload,
            ...(groups ? { $groups: groups } : {}),
          })
        })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerOrderViewed = (
    payload: Oms.StoreOrder & { appName?: string; isPurchaser: boolean },
  ) => {
    try {
      if (meta?.customerSegmentEnabled) {
        const products = (payload.orderItems || []).map((item) => {
          return {
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            id: item.id,
          }
        })
        const amount = {
          charges: (payload.taxAndCharges || [])
            .filter((item) => !item.description.includes('GST'))
            .reduce((sum, t) => sum + t.amount, 0),
          discount: (payload.orderPromotions || []).reduce(
            (sum, t) => sum + t.amount,
            0,
          ),
          tax:
            (payload.taxAndCharges || []).filter((item) =>
              item.description.includes('GST'),
            )?.[0]?.amount || 0,
          taxAndCharges: (payload.taxAndCharges || []).reduce(
            (sum, t) => sum + t.amount,
            0,
          ),
          amountProducts: (products || []).reduce((sum, t) => sum + t.price, 0),
        }
        const orderViewedPayload = {
          isPurchaser: payload.isPurchaser,
          app: {
            id: ODDLE_PASS_CLIENT_ID,
            name,
          },
          actor: 'consumer',
          orderNumber: payload.orderNumber,
          isPickup: payload.isPickup,
          submittedDate: payload.orderDate,
          accountId: meta.menu?.account?.id,
          accountName: meta.menu?.account?.name,
          menuId: meta.menu.id,
          menuName: meta.menu.name,
          storeId: payload.storeId,
          storeName: payload.storeName,
          promoCode: payload?.orderPromotions
            ? payload?.orderPromotions[0]?.promoCode
            : '',
          orderId: payload.id,
          charge: amount.charges,
          delivery: payload.deliveryCost,
          deliveryWithTaxCharge: amount.taxAndCharges + payload.deliveryCost,
          discount: amount.discount,
          discountWithTaxCharge: amount.discount + amount.tax,
          subTotal: payload.subtotal,
          subTotalWithCharge: payload.subtotal + amount.tax,
          tax: amount.tax,
          total: payload.total,
          currency: payload.currencyCode,
          products: {
            productItems: products,
            amount: amount.amountProducts,
          },
          brandId: brandIdOf(meta),
          brandName: meta.menu?.name,
        }
        safeSegment((analytics) =>
          analytics.track('Order Viewed', orderViewedPayload, {
            context: { groupId: brandIdOf(meta) },
          }),
        )
        safePosthog((ph) => {
          const groups = buildPosthogGroups(meta)
          ph.capture('Order Viewed', {
            ...orderViewedPayload,
            ...(groups ? { $groups: groups } : {}),
          })
        })
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyViewCart = (storeOrderData: Oms.StoreOrder) => {
    try {
      const payload = {
        currency: storeOrderData.currencyCode,
        value: storeOrderData.total,
        items: storeOrderData.orderItems?.map((orderItem) => ({
          item_id: orderItem.itemId,
          item_name: orderItem.name,
          quantity: orderItem.quantity,
          price: orderItem.price,
          item_brand: meta?.menu?.name,
          item_category: (
            menuData?.find((category) =>
              category.items.find((item) => item.id === orderItem.itemId),
            ) || {}
          ).name,
        })),
      }
      safeGtag('event', 'view_cart', payload)
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyLogin = (method: string) => {
    try {
      safeGtag('event', 'login', { method })
    } catch (ex) {
      console.error(ex)
    }
  }

  return {
    notifyPageView,
    notifyViewContent,
    notifyInitiateCheckout,
    notifyPurchase,
    segmentTrackerCheckoutStarted,
    segmentTrackerSignedOut,
    segmentTrackerSignedIn,
    segmentTrackerMenuViewed,
    segmentTrackerOrderReminderSaved,
    segmentTrackerOrderedShare,
    segmentTrackerOrderViewed,
    notifyAddToCart,
    notifyNewsletterCompleteRegistration,
    notifyNewsletterLead,
    notifyAccountCompleteRegistration,
    notifyAccountLead,
    notifyViewCart,
    notifyLogin,
  }
}

export default useTracker
